import { format } from 'date-fns';
import { uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import Card from 'antd/lib/card';
import Tag from 'antd/lib/tag';
import App from 'antd/lib/app';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import { fetchEvents } from '../../../../domain/event';
import { IEvent } from '../../../../domain/event/interface';
import useAbortableFetch from '../../../../domain/useAbortableFetch';
import { GroupedEvent, groupEvents } from '../../../../domain/event/calendarEvent';
import { EventGridCellTooltip } from '../../../events/components/EventGridCellTooltip/EventGridCellTooltip';
import { getComputerFormattedTime } from '../../../../domain/date/date';

import './EventsDateWidget.css';

interface IEventsDateWidget {
    company?: number | null;
    title: string;
    date: {
        start: Date;
        end: Date;
    };
    onActiveEventsHandle: Function;
}

export const EventsDateWidget = ({ company, title, date, onActiveEventsHandle }: IEventsDateWidget) => {
    const { notification } = App.useApp();
    const [events, setEvents] = useState<GroupedEvent[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [abortableFetchEvents] = useAbortableFetch(fetchEvents);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await abortableFetchEvents({
                    companyId: company,
                    start: date.start,
                    end: date.end,
                    include: 'site',
                    event_cancelled: false,
                    pagination: { pageSize: 15000, current: 1 },
                });

                const groupedEvents = groupEvents(data);

                setEvents(groupedEvents);

                const uniqMarkets = uniq(data.map((event: IEvent) => event?.site?.lmp_market));

                if (uniqMarkets?.length) {
                    onActiveEventsHandle(uniqMarkets);
                }
            } catch (error: any) {
                if (error.name === 'AbortError') return;
                notification.error({ key: 'events-info', message: error.message || 'Cannot get events' });
            }
            setLoading(false);
        })();
    }, [company, date]);

    const renderEventInfo = (event: GroupedEvent) => {
        const eventStart = event.extendedProps.startTimeArray
            ?.map((datetime: Date) => {
                return format(datetime, 'h:mm a');
            })
            .join('; ');
        const eventPrograms = event.title;
        const sitesCount = event.extendedProps.sitesCount;

        return (
            <Popover
                title={
                    <EventGridCellTooltip
                        eventStartTime={eventStart}
                        eventProgram={eventPrograms}
                        sitesCount={sitesCount}
                        noEndTime={event.extendedProps.noEndTime}
                        eventDuration={event.extendedProps.duration}
                        standbyPeriodDuration={event.extendedProps.standbyPeriodDuratio}
                    />
                }
                key={`${eventStart}-${eventPrograms}`}
            >
                <Tag
                    className={`${event.classNames.join(' ')}`}
                    style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 }}
                    key={event.id}
                >
                    {eventStart} {getComputerFormattedTime(date?.end || new Date(), 'zzz')}
                    <span style={{ margin: '0 4px' }}>-</span>
                    {eventPrograms.length > 0 && (
                        <>
                            {eventPrograms}
                            <span style={{ margin: '0 4px' }}>-</span>
                        </>
                    )}
                    {sitesCount} {sitesCount > 1 ? 'Sites' : 'Site'}{' '}
                </Tag>
            </Popover>
        );
    };

    return (
        <Card
            title={
                <>
                    {title}
                    <Tooltip
                        title={`Events on this card are displayed on your computer's timezone - ${getComputerFormattedTime(date?.end || new Date(), 'zzz')}, but events are dispatched at each site's local timezone`}
                    >
                        <InfoCircleOutlined style={{ color: 'var(--ant-color-primary)', marginLeft: '5px' }} />
                    </Tooltip>
                </>
            }
            className={'events-date-widget'}
            extra={format(date.start, 'LLL dd')}
            loading={loading}
            style={{ overflow: 'hidden' }}
            styles={{
                body: { height: '135px', overflow: 'auto', padding: '10px 24px', marginBottom: '5px' },
            }}
        >
            {events.length ? events.map(event => renderEventInfo(event)) : <Tag> None Currently Scheduled </Tag>}
        </Card>
    );
};
